import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryPasswordStep1 } from "@ui-kit/components/index.es";
import withExchangeToken from "pages/_components/withExchangeToken";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import { selectors as loginSelectors } from "reducers/login";
import { actions as fingerprintActions, selectors as fingerprintSelectors } from "reducers/fingerprint";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { isMobileNative } from "util/device";
import Yup from "yup";

const FORM_ID = "recoveryPassword.step1";

function RecoveryPasswordStep1V3({
    dispatch,
    history,
    location,
    maskedEmail,
    maskedPhone,
    userName,
    exchangeToken,
    recoveryAsyncPending,
    fieldErrors,
    fingerprintAvailable,
    isTokenSeedAvailable,
    fetchingToken,
}) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryPassword"]));
        if (isMobileNative) {
            dispatch(fingerprintActions.fingerprintAvailability());
            dispatch(tokenActions.verifyTokenSeedExist());
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const showBiometricBtn = useMemo(() => {
        if (!isMobileNative) {
            return false;
        }
        const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
        const useBiometricTokenAccess = configUtils.getBoolean("backend.show.Biometric.Token.offline");
        const fingerprintEnabled = configUtils.getBoolean("frontend.login.fingerprint.enabled");
        return useOfflineToken && fingerprintAvailable && fingerprintEnabled && useBiometricTokenAccess;
    }, [fingerprintAvailable]);

    const channelOptions = useMemo(
        () => [
            { value: "phone", label: i18n.get(`${FORM_ID}.device.phone`), maskedData: maskedPhone || "" },
            { value: "email", label: i18n.get(`${FORM_ID}.device.email`), maskedData: maskedEmail || "" },
        ],
        [maskedEmail, maskedPhone],
    );

    const initialValues = useMemo(
        () => ({
            sendChannel: "phone",
            secondFactor: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                sendChannel: Yup.string().required(i18n.get(`${FORM_ID}.sendChannel.required`)),
                ...(!showBiometricBtn
                    ? {
                          secondFactor: Yup.string().required(
                              i18n.get("settings.token.disabledTokenPass.password.required"),
                          ),
                      }
                    : {}),
            }),
        [showBiometricBtn],
    );

    const handleBiometricSubmit = useCallback(
        (_e, formikProps) => {
            if (isTokenSeedAvailable) {
                const form = {
                    setValues: formikProps.setValues,
                    values: formikProps.values,
                };
                const field = { name: "secondFactor" };
                dispatch(tokenActions.generateBiometricsToken(formikProps.submitForm, form, field));
            } else {
                history.push("/v3/recoveryPassword/informationBiometrics");
            }
        },
        [dispatch, isTokenSeedAvailable, history],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const channel = typeof values.sendChannel === "object" ? values.sendChannel?.value : values.sendChannel;
            const secondFactor = values.secondFactor ?? "";

            const enhancedBag = {
                ...formikBag,
                props: {
                    ...(formikBag.props || {}),
                    location: { pathname: "/v3/recoveryPassword/step1" },
                    history,
                    isMobileNative,
                },
            };

            dispatch(
                recoveryPasswordActions.recoveryPassStep1(
                    channel,
                    userName || "",
                    secondFactor,
                    enhancedBag,
                    exchangeToken,
                ),
            );
        },
        [dispatch, userName, history, exchangeToken],
    );

    const handleHaveCode = useCallback(() => {
        history.push("/v3/recoveryPassword/step2");
    }, [history]);

    const handleNoToken = useCallback(() => {
        history.push("/v3/recoveryPassword/informationClaveToken");
    }, [history]);

    const handleNoneOfTheAbove = useCallback(() => {
        history.push("/v3/recoveryPassword/information");
    }, [history]);

    const getLabels = useCallback((id) => {
        const keys = {
            sendChannel: `${FORM_ID}.sendChannel`,
            secondFactor: "recoveryPassword.step1.secondFactor.label",
            continue: "global.continue",
            haveCode: "recoveryPassword.step1.userHasCode",
            noAccess: "recoveryPassword.step1.device.none",
            noToken: "recoveryPassword.step1.token.V3.none",
            biometric: "biometrics.recovery.label",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback((id) => i18n.get(id), []);
    const isFromLoginStep2 = location?.state?.fromLoginStep2 === true;
    const headerKey = isFromLoginStep2 ? "recoveryPassword.step1.only.pass.header" : "recoveryPassword.step1.header";

    return (
        <RecoveryPasswordStep1
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            channelOptions={channelOptions}
            handleHaveCode={handleHaveCode}
            handleNoToken={handleNoToken}
            handleNoneOfTheAbove={handleNoneOfTheAbove}
            getLabels={getLabels}
            getText={getText}
            headerKey={headerKey}
            submitLoading={recoveryAsyncPending}
            fieldErrors={fieldErrors ?? undefined}
            enableBiometric={showBiometricBtn}
            handleBiometricSubmit={showBiometricBtn ? handleBiometricSubmit : undefined}
            biometricLoading={fetchingToken}
        />
    );
}

RecoveryPasswordStep1V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    location: shape({ state: shape({ fromLoginStep2: bool }) }),
    maskedEmail: string,
    maskedPhone: string,
    userName: string,
    exchangeToken: string,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
    fingerprintAvailable: bool,
    isTokenSeedAvailable: bool,
    fetchingToken: bool,
};

RecoveryPasswordStep1V3.defaultProps = {
    maskedEmail: "",
    maskedPhone: "",
    location: null,
    userName: "",
    exchangeToken: null,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
    fingerprintAvailable: false,
    isTokenSeedAvailable: false,
    fetchingToken: false,
};

const mapStateToProps = (state) => ({
    maskedEmail: loginSelectors.getMaskedEmail(state),
    maskedPhone: loginSelectors.getMaskedPhone(state),
    userName: loginSelectors.getUsername(state),
    exchangeToken: recoveryPasswordSelectors.getExchangeToken(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
    fingerprintAvailable: fingerprintSelectors.getAvailability(state),
    isTokenSeedAvailable: tokenSelectors.getIsTokenSeedAvailable(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
});

export default compose(connect(mapStateToProps), withExchangeToken)(RecoveryPasswordStep1V3);
